/**
 * A minimal DXF R12 (AC1009) ASCII writer.
 *
 * R12 by hand rather than a library: DXF R12 is plain group-code text, the
 * entities a cross-section drawing needs are POLYLINE/VERTEX, LINE and TEXT,
 * and R12 is the dialect every CAD tool opens without negotiation.
 * `LWPOLYLINE` and `HATCH` are R13+ and are deliberately absent — cut and fill
 * ship as closed boundary polylines that the operator hatches in their own
 * template, which is what a surveyor does with them anyway.
 *
 * A group code and its value are two lines: the code, then the value. Nothing
 * else in the format matters for what we emit.
 */

export interface DxfLayer {
  name: string;
  /** AutoCAD Color Index. 1 red, 2 yellow, 3 green, 4 cyan, 5 blue, 7 fg, 8 grey. */
  color: number;
}

export interface DxfPoint {
  x: number;
  y: number;
  z: number;
}

const g = (code: number, value: string | number): string => `${code}\n${value}\n`;

/**
 * DXF R12 layer names have no escaping mechanism, so they are constrained to a
 * safe subset rather than quoted. 31 characters is the R12 name limit.
 */
export const layerName = (s: string): string =>
  s
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '_')
    .slice(0, 31) || 'LAYER';

/** Six decimals is a tenth of a millimetre at UTM scale — past survey precision. */
const num = (v: number): string => (Number.isFinite(v) ? v.toFixed(6) : '0.000000');

const finitePoint = (p: DxfPoint): boolean =>
  Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);

/**
 * A 3D POLYLINE. Vertices that are not finite are DROPPED rather than written:
 * the literal `NaN` is rejected by every CAD reader, and silently discarding
 * the whole polyline would hide a real section instead of a bad sample.
 *
 * Fewer than two surviving vertices produces nothing — a lone point is not a
 * line, and an empty string is filtered out by `dxfDocument`.
 */
export function dxfPolyline(pts: readonly DxfPoint[], opts: { layer: string; closed?: boolean }): string {
  const clean = pts.filter(finitePoint);
  if (clean.length < 2) return '';
  const layer = layerName(opts.layer);
  // 8 = this is a 3D polyline; 1 = closed.
  const flags = 8 | (opts.closed ? 1 : 0);
  let out =
    g(0, 'POLYLINE') + g(8, layer) + g(66, 1) + g(70, flags) + g(10, '0.0') + g(20, '0.0') + g(30, '0.0');
  for (const p of clean) {
    // 32 = 3D polyline vertex; required when the owning POLYLINE is 3D.
    out += g(0, 'VERTEX') + g(8, layer) + g(10, num(p.x)) + g(20, num(p.y)) + g(30, num(p.z)) + g(70, 32);
  }
  return out + g(0, 'SEQEND') + g(8, layer);
}

export function dxfLine(a: DxfPoint, b: DxfPoint, layer: string): string {
  if (!finitePoint(a) || !finitePoint(b)) return '';
  return (
    g(0, 'LINE') +
    g(8, layerName(layer)) +
    g(10, num(a.x)) +
    g(20, num(a.y)) +
    g(30, num(a.z)) +
    g(11, num(b.x)) +
    g(21, num(b.y)) +
    g(31, num(b.z))
  );
}

/** Horizontal justification about the insertion point. */
export type DxfTextAlign = 'left' | 'center' | 'right';

/**
 * Single-line TEXT. Newlines are flattened to spaces — R12 TEXT is one line by
 * definition, and MTEXT is R13+.
 *
 * Justification matters because a drawing has no text metrics: a left-aligned
 * label placed at a cell's right edge runs into the next cell, and the width it
 * runs by depends on the reader's font. Anchoring the label to the edge it must
 * not cross is the only way to say "inside this cell" in DXF.
 */
export interface DxfTextOptions {
  /** Elevation of the insertion point. Sheet and plan layouts leave it at 0;
   *  a model-space label has to sit at the height of what it names. */
  z?: number;
  /** Degrees CCW about the insertion point, in the XY plane. */
  rotation?: number;
}

export function dxfText(
  x: number,
  y: number,
  height: number,
  text: string,
  layer: string,
  align: DxfTextAlign = 'left',
  opts: DxfTextOptions = {},
): string {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return '';
  const z = Number.isFinite(opts.z) ? (opts.z as number) : 0;
  const rotation = Number.isFinite(opts.rotation) ? (opts.rotation as number) : 0;
  let out =
    g(0, 'TEXT') +
    g(8, layerName(layer)) +
    g(10, num(x)) +
    g(20, num(y)) +
    g(30, num(z)) +
    g(40, num(height)) +
    g(1, text.replace(/[\r\n]+/g, ' '));
  // Written only when it is one: an explicit 0 rotation on every label of a
  // sheet is noise in a format that is already two lines per number.
  if (rotation !== 0) out += g(50, num(rotation));
  if (align === 'left') return out;
  // R12: 72 is the horizontal justification, and once it is non-zero the
  // SECOND alignment point (11/21) is the one that positions the text — 10/20
  // is ignored. Both are written, so a reader that honours either lands the
  // text in the same place.
  return out + g(72, align === 'center' ? 1 : 2) + g(11, num(x)) + g(21, num(y)) + g(31, num(z));
}

/**
 * HEADER variables that declare the drawing METRIC, in metres.
 *
 * `sectionDxf` plots one drawing unit per metre, but a DXF that doesn't say so
 * is "unitless": inserting or attaching it into a millimetre or an imperial
 * drawing applies whatever default conversion the receiving CAD is configured
 * with — silently, and by 1000 or 25.4. `$MEASUREMENT` is the separate switch
 * that picks the metric linetype and hatch-pattern files (`acadiso.lin` /
 * `acadiso.pat`); with no value it defaults to 0, English.
 *
 * `$INSUNITS` and `$MEASUREMENT` postdate R12, but a DXF reader is required to
 * skip header variables it does not know, and every reader that DOES know them
 * then places the drawing correctly — which is the whole point.
 */
const HEADER_VARS: readonly [name: string, code: number, value: string | number][] = [
  ['$ACADVER', 1, 'AC1009'],
  ['$INSUNITS', 70, 6], // 6 = meters
  ['$MEASUREMENT', 70, 1], // 1 = metric
  ['$LUNITS', 70, 2], // 2 = decimal (4 would be architectural: feet and inches)
  ['$AUNITS', 70, 0], // 0 = decimal degrees
];

export function dxfDocument(layers: readonly DxfLayer[], entities: readonly string[]): string {
  let out = g(0, 'SECTION') + g(2, 'HEADER');
  for (const [name, code, value] of HEADER_VARS) out += g(9, name) + g(code, value);
  out += g(0, 'ENDSEC');
  out += g(0, 'SECTION') + g(2, 'TABLES') + g(0, 'TABLE') + g(2, 'LAYER') + g(70, layers.length);
  for (const l of layers) {
    out += g(0, 'LAYER') + g(2, layerName(l.name)) + g(70, 0) + g(62, l.color) + g(6, 'CONTINUOUS');
  }
  out += g(0, 'ENDTAB') + g(0, 'ENDSEC');
  out += g(0, 'SECTION') + g(2, 'ENTITIES') + entities.filter(Boolean).join('') + g(0, 'ENDSEC');
  return out + g(0, 'EOF');
}

/**
 * Reads back what this module writes: entity type, layer, and vertex count.
 *
 * Exported for tests. A golden-string assertion can pass for a file no CAD
 * tool will open; walking the group codes back out proves the structure is
 * real. It understands only the entities above, which is all it needs to.
 */
export function parseDxfEntities(dxf: string): { type: string; layer: string; vertices: number }[] {
  const lines = dxf.split('\n');
  const out: { type: string; layer: string; vertices: number }[] = [];
  let inEntities = false;
  let current: { type: string; layer: string; vertices: number } | null = null;

  const flush = () => {
    if (current) out.push(current);
    current = null;
  };

  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1];
    if (code === '2' && value === 'ENTITIES') {
      inEntities = true;
      continue;
    }
    if (!inEntities) continue;
    if (code === '0') {
      if (value === 'VERTEX') {
        if (current) current.vertices += 1;
      } else if (value === 'SEQEND') {
        flush();
      } else if (value === 'ENDSEC' || value === 'EOF') {
        flush();
        inEntities = false;
      } else {
        flush();
        // A POLYLINE counts its VERTEX children; LINE and TEXT have their
        // geometry inline, so their count is fixed.
        current = { type: value, layer: '', vertices: value === 'POLYLINE' ? 0 : value === 'LINE' ? 2 : 1 };
      }
    } else if (code === '8' && current && !current.layer) {
      current.layer = value;
    }
  }
  flush();
  return out;
}
